<script lang="ts" context="module">
  export type SubmitEvent = { value: string; cancelLoading: () => void };
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { alerts } from "$stores/alert";
  import Modal from "./Modal.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";

  export let open: boolean;
  export let title: string;
  export let message = "";
  export let submessage = "";
  export let confirmLabel: string = "Confirm";
  export let value: string = "";
  export let loading: boolean = false;
  export let blankErrorMessage: string = "";

  // Cancel loading state of confirm/submit button
  const cancelLoading = () => {
    loading = false;
  };

  $: if (!open) {
    cancelLoading();
  }

  const dispatch = createEventDispatcher<{
    submit: SubmitEvent;
  }>();

  const handleConfirm = () => {
    // display error if input blank
    if (blankErrorMessage) {
      if (!value) {
        alerts.error(blankErrorMessage);
        loading = false;
        return;
      }
      loading = true;
      dispatch("submit", { value, cancelLoading });
    } else {
      loading = true;
      dispatch("submit", { value, cancelLoading });
    }
    open = false;
    value = "";
  };
</script>

<svelte:window
  on:keydown={(e) => {
    if (open && e.metaKey && e.key == "Enter") {
      handleConfirm();
      open = false;
    } else if (open && e.key == "Escape") {
      open = false;
    }
  }}
/>

<Modal bind:open maxWidth="max-w-[750px]">
  <div class="flex flex-col gap-6 p-6">
    <h3 class="text-sm font-medium text-gray-600 dark:text-gray-200">{title}</h3>
    {#if message}<p class="mt-2 text-sm font-medium text-gray-500 dark:text-gray-300">
        {message}
      </p>{/if}

    <label for="version-comment" class="sr-only">Add version comment</label>
    <!-- svelte-ignore a11y-autofocus -->
    <textarea
      bind:value
      id="version-comment"
      autofocus
      rows="6"
      placeholder="Add version comment..."
      class="focus:ring-none w-full rounded-md border border-black/10 bg-transparent p-2 text-[13px] font-medium text-gray-600 placeholder:text-gray-400 focus:outline-none dark:border-white/10 dark:bg-gray-700/80 dark:text-gray-100 dark:placeholder:text-gray-500"
      on:keydown={(e) => {
        if (e.metaKey && e.key === "Enter") {
          handleConfirm();
          open = false;
        }
      }}
    />
    {#if submessage}
      <p class="text-xs font-medium text-gray-500/70 dark:text-gray-400">
        {submessage}
      </p>
    {/if}

    <div class="dark:transparent flex w-full gap-3 pt-2">
      <Button
        on:click={() => (open = false)}
        variant="secondary"
        label="Cancel"
        size="large"
        isFullWidth
      />
      <Button
        on:click={handleConfirm}
        variant="system"
        size="large"
        label={confirmLabel}
        disabled={loading}
        isFullWidth
      />
    </div>
  </div>
</Modal>
